// How the affordability check (NAMFISA ML.S.4) is shown: the decision's
// colours and words, the reasons in severity order, who may override, and
// the qualification table's rows. Pure, so it is tested on its own
// (affordabilityView.test.js); AffordabilityCheck.jsx lays it out.
import { toCurrency } from '../../utils/adminMetrics';

// The calculator panel's outcome look: a small badge and a thin coloured
// edge on a plain panel. Shared with CalculatorDecline.jsx.
export const OUTCOME_STYLES = {
  // Declined: not always "cannot afford" (no bureau report, a salary that
  // does not match), so the badge says what happened, not why.
  declined: { label: 'Declined', badge: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/60', edge: 'border-l-rose-300 dark:border-l-rose-700' },
  referred: { label: 'Check first', badge: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/60', edge: 'border-l-amber-300 dark:border-l-amber-700' },
  reduced: { label: 'Lower amount', badge: 'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800/60', edge: 'border-l-sky-300 dark:border-l-sky-700' },
  approved: { label: 'Qualifies', badge: 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/60', edge: 'border-l-emerald-300 dark:border-l-emerald-700' },
  // Before the credit bureau report: never "approved", only pre-qualified.
  prequalified: { label: 'Pre-qualified', badge: 'bg-indigo-50 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800/60', edge: 'border-l-indigo-300 dark:border-l-indigo-700' },
  pending: { label: 'Not finished', badge: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700', edge: 'border-l-slate-300 dark:border-l-slate-600' },
};

// Each decision: green Approved, amber Manual review, red Declined.
export const DECISIONS = {
  Approved: { label: 'Approved', colour: 'green', style: OUTCOME_STYLES.approved },
  ManualReview: { label: 'Manual review', colour: 'amber', style: OUTCOME_STYLES.referred },
  Declined: { label: 'Declined', colour: 'red', style: OUTCOME_STYLES.declined },
};
export const decisionView = (decision) => DECISIONS[decision] || null;

/** A pre-qualification: no credit bureau report yet, and not declined. */
export const isPreQualified = (record) => record?.subject_to_bureau === true && record?.decision !== 'Declined';
const PRE_QUALIFIED = { label: 'Pre-qualified', colour: 'indigo', style: OUTCOME_STYLES.prequalified };
/** The record's badge: Pre-qualified before the bureau report, else its decision. */
export const recordView = (record) => (isPreQualified(record) ? PRE_QUALIFIED : decisionView(record?.decision));

export const SEVERITY_ORDER = { Decline: 0, Review: 1, Info: 2 };
export const SEVERITY_TAGS = {
  Decline: { label: 'Decline', className: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/60' },
  Review: { label: 'Review', className: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/60' },
  Info: { label: 'Note', className: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700' },
};
export const SOURCE_LABELS = { Payslip: 'Payslip', Statement: 'Bank statement', Bureau: 'Credit bureau', Calculation: 'Calculation' };

/** The reasons, declines first, then reviews, then notes (stable within each). */
export const sortReasons = (reasons) => (Array.isArray(reasons) ? reasons : [])
  .map((r, i) => ({ r, i }))
  .sort((a, b) => ((SEVERITY_ORDER[a.r.severity] ?? 3) - (SEVERITY_ORDER[b.r.severity] ?? 3)) || (a.i - b.i))
  .map(({ r }) => r);

/** The banner's one sentence. */
export const bannerSentence = (record) => {
  const decision = record?.decision;
  if (decision === 'Declined') {
    const n = (record.reasons || []).filter((r) => r.severity === 'Decline').length || (record.reasons || []).length;
    return `Does not qualify. ${n} reason${n === 1 ? '' : 's'} below.`;
  }
  // "Pre-qualified up to N$X, subject to credit bureau check", never "Approved".
  if (isPreQualified(record) && record.label) return `${record.label}.`;
  const max = record?.qualification?.recommended_max_instalment;
  return max !== null && max !== undefined ? `Qualifies for an instalment of up to ${toCurrency(max)}.` : '';
};

export const OVERRIDE_PERMISSION = 'OverrideAffordability';
export const hasOverridePermission = (permissions) => {
  const perms = Array.isArray(permissions) ? permissions : [];
  return perms.includes('*') || perms.includes(OVERRIDE_PERMISSION);
};
/**
 * The override panel: only for a Declined or ManualReview decision, only for
 * permitted users, and never on a pre-qualification (the bureau report comes
 * first; record: the affordability record, when known).
 */
export const canOverride = (permissions, decision, record = null) => (decision === 'Declined' || decision === 'ManualReview')
  && hasOverridePermission(permissions) && !isPreQualified(record);

/**
 * The qualification table, in the order ML.S.4 works it out. highlight:
 * 'binding' (the cap that sets the statutory maximum) or 'over' (the
 * proposed instalment above the recommended or statutory maximum).
 */
export const qualificationRows = (q) => {
  if (!q) return [];
  const proposed = q.proposed_instalment;
  const over = proposed !== null && proposed !== undefined
    && ((q.recommended_max_instalment !== null && proposed > q.recommended_max_instalment)
      || (q.statutory_max_instalment !== null && proposed > q.statutory_max_instalment));
  return [
    { key: 'gross_pay', label: 'Gross pay', value: q.gross_pay },
    { key: 'net_pay', label: 'Net pay', value: q.net_pay },
    { key: 'credit_obligations', label: 'Credit obligations', value: q.credit_obligations, sign: '-' },
    { key: 'basic_necessities', label: 'Basic necessities', value: q.basic_necessities, sign: '-' },
    { key: 'discretionary_income', label: 'Discretionary income', value: q.discretionary_income, strong: true },
    { key: 'cap_a', label: 'Cap A (one third of gross)', value: q.cap_a, highlight: q.binding_cap === 'A' ? 'binding' : null },
    { key: 'cap_b', label: 'Cap B (50% of discretionary)', value: q.cap_b, highlight: q.binding_cap === 'B' ? 'binding' : null },
    { key: 'statutory_max_instalment', label: 'Statutory maximum instalment', value: q.statutory_max_instalment, strong: true },
    // Before the risk band's share, when the band lowered it.
    ...(q.prudential_recommended_instalment !== null && q.prudential_recommended_instalment !== undefined
      && q.prudential_recommended_instalment !== q.recommended_max_instalment
      ? [{ key: 'prudential_recommended_instalment', label: 'Recommended before the risk band', value: q.prudential_recommended_instalment }]
      : []),
    { key: 'recommended_max_instalment', label: q.risk_band ? `Recommended maximum instalment (risk band ${q.risk_band})` : 'Recommended maximum instalment', value: q.recommended_max_instalment, strong: true },
    { key: 'proposed_instalment', label: 'Proposed instalment', value: proposed, highlight: over ? 'over' : null },
  ];
};

/** The other decisions an officer can move to. */
export const overrideChoices = (decision) => Object.keys(DECISIONS).filter((d) => d !== decision);

// The words the check puts before each of its own reasons in the saved
// decision lines ("Declined: Title. Detail"): those lines are shown from the
// reasons themselves.
const FINDING_LINE = /^(Declined|For review|Note): /;
// The other lines: document checks and officer notes by their first words.
const LINE_SEVERITY = [
  [/^Failed check:/i, 'Decline'],
  [/^(Officer review|Review|Payslip check):/i, 'Review'],
];

/**
 * Everything to show as reasons, in the staff screen's shape: the check's
 * reasons, then every other saved decision line (document checks, test
 * account, how the maximum was worked out) as a reason of its own: "Failed
 * check" a decline, an officer note a review, the rest a note.
 */
export const reasonsForDisplay = (reasons, lines = []) => {
  const own = Array.isArray(reasons) ? reasons : [];
  const rest = (Array.isArray(lines) ? lines : [])
    .filter((l) => typeof l === 'string' && l.trim())
    .filter((l) => !(own.length && FINDING_LINE.test(l)))
    .map((line, i) => {
      const m = /^([A-Z][A-Za-z ]{1,40}):\s+(.+)$/s.exec(line.trim());
      const severity = (LINE_SEVERITY.find(([rx]) => rx.test(line)) || [null, 'Info'])[1];
      return { code: `LINE_${i}`, title: m ? m[1] : 'How it was worked out', detail: m ? m[2] : line.trim(), severity, source: null };
    });
  return sortReasons([...own, ...rest]);
};

// ── Koniza's internal risk rating (scorecard v3) ────────────────────────────
// On top of the NAMFISA test, and only ever lowering the offer.
export const RISK_BAND_STYLES = {
  A: 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/60',
  B: 'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800/60',
  C: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/60',
  D: 'bg-orange-50 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800/60',
  E: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/60',
};

const sharePct = (share) => `${Math.round(Number(share) * 100)}%`;

/** What the band does to the offer, in one or two sentences. */
export const riskEffectSentence = (rating) => {
  if (!rating) return '';
  if (rating.enabled === false) return 'Shown for information: the rating is switched off and changes no amount.';
  const parts = [];
  if (rating.band === 'E') parts.push('Koniza does not lend at this level.');
  else {
    const term = `up to ${rating.max_term_months} month${Number(rating.max_term_months) === 1 ? '' : 's'}`;
    parts.push(`Offers ${sharePct(rating.instalment_share)} of the recommended instalment, ${term}${rating.band === 'D' ? '; an officer approves it' : ''}.`);
  }
  if (rating.first_loan && rating.first_loan_cap && rating.band !== 'E') parts.push(`First loan with us: at most ${toCurrency(rating.first_loan_cap)}.`);
  if (rating.band_cap_reason) parts.push(`${rating.band_cap_reason}, so band ${rating.band} rather than ${rating.scored_band}.`);
  return parts.join(' ');
};

/** The rating as shown: its badge, score and the six factors. null: none. */
export const riskRatingView = (rating) => {
  if (!rating || !rating.band) return null;
  const factors = Array.isArray(rating.factors) ? rating.factors : [];
  return {
    band: rating.band,
    label: rating.band_label || '',
    score: rating.score,
    badge: RISK_BAND_STYLES[rating.band] || RISK_BAND_STYLES.C,
    effect: riskEffectSentence(rating),
    // For the strip at the top: the risk level, the share of the recommended
    // instalment offered, the longest term and a first loan's cap.
    risk_level: rating.risk_level || null,
    share_pct: rating.enabled === false || rating.instalment_share === undefined || rating.instalment_share === null
      ? null : Math.round(Number(rating.instalment_share) * 100),
    max_term: rating.enabled === false ? null : (Number(rating.max_term_months) || 0),
    first_loan_cap: rating.enabled !== false && rating.first_loan && rating.first_loan_cap ? Number(rating.first_loan_cap) : null,
    enabled: rating.enabled !== false,
    factors: factors.map((f) => ({ key: f.key, label: f.label, points: f.points, max: f.max, detail: f.detail, full: f.points === f.max, none: f.points === 0 })),
    total_max: factors.reduce((s, f) => s + (Number(f.max) || 0), 0) || 100,
  };
};
